/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import Text from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '../common/Select';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import RunSpeakerIdMenu from './RunSpeakerIdMenu';

import i18n from '../../i18n';
import { useUserQuery } from '../../actions/UserActions';
import {
  useMeetingQuery,
  useMeetingActions,
} from '../../actions/MeetingActions';

const OUTPUT_FORMATS = [
  { value: 'transcript', label: i18n.t('Transkribering') },
  { value: 'subtitle', label: i18n.t('Undertextning') },
];


function isEditor(meeting, user) {
  if (user?.role === 'admin') {
    return true;
  }
  return meeting?.project_role === 'edit' || meeting?.project_role === 'admin';
}

export default function SettingsDialog({ data = {}, open, onClose }) {
  const { t } = useTranslation();
  const { user } = useUserQuery();
  const isGuest = !user || !user.role || user.role === 'guest';
  const { meetingId, navigate } = data;
  const { meeting } = useMeetingQuery(meetingId, isGuest);
  const { updateMeetingAsync, deleteMeetingAsync } =
    useMeetingActions(meetingId);
  const [name, setName] = useState(
    meeting?.name || meeting?.meeting_data?.name
  );
  const [format, setFormat] = useState(meeting.output_format || 'transcript');
  const [openDelete, setOpenDelete] = useState(false);
  const [menuOption, setMenuOption] = useState('default');

  const handleUpdateSettings = useCallback(
    async ({ name, format }) => {
      await updateMeetingAsync({ name, output_format: format });
    },
    [updateMeetingAsync]
  );

  const handleDeleteMeeting = useCallback(async () => {
    await deleteMeetingAsync();
    const folderPrefix = meeting?.folder_prefix;
    navigate(`/folders${folderPrefix}`);
    onClose();
  }, [deleteMeetingAsync, navigate, meeting, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {menuOption === 'default' ? (
        <React.Fragment>
        <DialogContent>
          <div
            id="meeting-aside-settings"
            css={css`
              display: flex;
              flex-direction: column;
              line-height: 1.5;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                margin-top: 1rem;
                padding: 0.5rem 0.5rem 0px;
              `}
            >
              <Text variant="subtitle">{t('Allmänna inställningar')}</Text>
              <Divider />
              <TextField
                css={css`
                  margin-top: 0.5rem;
                `}
                size="small"
                label={t('Namn')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Select
                css={css`
                  margin-top: 0.5rem;
                `}
                label={t('Formatering')}
                options={OUTPUT_FORMATS}
                size="small"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
              />
              <Button
                variant="contained"
                color="primary"
                css={css`
                  margin-right: auto;
                  margin-top: 0.5rem;
                `}
                startIcon={<SaveIcon />}
                disabled={
                  name === meeting.name &&
                  format === (meeting.output_format || 'transcript')
                }
                onClick={() => handleUpdateSettings({ name, format })}
              >
                {t('Spara')}
              </Button>
            </div>
            {isEditor(meeting, user) && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                margin-top: 1rem;
                padding: 0.5rem 0.5rem 0px;
              `}
            >
              <Text variant="subtitle">{t('Talaridentifikation')}</Text>
              <Divider />
              <Text variant="body2">
                {t(
                  'Kör om talaridentifikationen med andra inställningar.'
                )}
              </Text>
              <div id="meeting-aside-speaker-id-menu">
                <Button
                  onClick={() => setMenuOption('speakerId')}
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                >
                  {t('Kör om talaridentifikation')}
                </Button>
              </div>
            </div>
            )}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                margin-top: 1rem;
                padding: 0.5rem 0.5rem 0px;
              `}
            >
              <Text variant="subtitle">{t('Radera transkribering')}</Text>
              <Divider />
              <Text variant="body2">
                {t(
                  'Om du raderar transkriberingen kommer all data att försvinna och kan inte återställas.'
                )}
              </Text>
              <Button
                variant="contained"
                color="error"
                css={css`
                  margin-right: auto;
                `}
                startIcon={<DeleteIcon />}
                onClick={() => setOpenDelete(true)}
              >
                {t('Radera transkribering')}
              </Button>
            </div>
            <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
              <DialogTitle>{t('Radera transkribering')}</DialogTitle>
              <DialogContent>
                <Text variant="body2">
                  {t(
                    'Är du säker på att du vill radera transkriberingen? All data kommer att försvinna och kan inte återställas.'
                  )}
                </Text>
              </DialogContent>
              <DialogActions>
                <Button variant="outlined" onClick={() => setOpenDelete(false)}>
                  {t('Avbryt')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleDeleteMeeting()}
                >
                  {t('Radera')}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Stäng')}</Button>
        </DialogActions>
      </React.Fragment>
    ) : menuOption === 'speakerId' ? (
        <RunSpeakerIdMenu
          meetingId={meetingId}
          source={meeting.source}
          meetingBotSpeakers={meeting.meeting_bot_speakers}
          toSettings={() => setMenuOption('default')}
          closeSettings={() => {
            setMenuOption('default')
            onClose();
          }}
        />
    ) : null}
    </Dialog>
  );
}
